import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, Hash, Images, Eye, UnlockKeyhole } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-500",
  pending: "bg-slate-100 text-slate-600",
  printing: "bg-blue-100 text-blue-700",
  framing: "bg-amber-100 text-amber-700",
  dispatched: "bg-emerald-100 text-emerald-700",
};

export default function AdminMRDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mr, setMr] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/employees/${id}`),
      api.get(`/requests?mine=false&mr_id=${id}`),
    ])
      .then(([e, r]) => { setMr(e.data); setRequests(r.data); })
      .catch(() => toast.error("Failed to load MR details"))
      .finally(() => setLoading(false));
  }, [id]);

  const counts = requests.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  const resetRequestCount = async () => {
    if (!mr) return;
    if (!window.confirm(`Reset request counter for ${mr.name} (${mr.emp_code}) to 0?`)) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/employees/${mr.id}/reset-request-count`, { count: 0 });
      setMr((prev) => ({
        ...prev,
        request_seq_counter: data.request_seq_counter,
        request_limit: data.limit,
      }));
      toast.success("Request counter reset to 0");
    } catch {
      toast.error("Failed to reset request counter");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <Shell role="admin">
      <Button variant="ghost" size="sm" className="mb-4 gap-1 -ml-2" onClick={() => nav(-1)}>
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      {loading ? (
        <div className="text-slate-400 py-10 text-center">Loading…</div>
      ) : !mr ? (
        <div className="text-slate-400 py-10 text-center">MR not found.</div>
      ) : (
        <>
          {/* MR header */}
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">MR Details</div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-brand-800 grid place-items-center text-white font-bold text-xl">
                {mr.name?.[0] || "U"}
              </div>
              <div>
                <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{mr.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={mr.role === "admin" ? "default" : "secondary"}>{mr.role}</Badge>
                  <Badge className={mr.status === "active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}>{mr.status}</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Info + stats */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card className="p-5 space-y-3">
              <InfoRow icon={Hash} label="EMP Code" value={mr.emp_code} />
              <InfoRow icon={Mail} label="Email" value={mr.email || "—"} />
              <InfoRow icon={Phone} label="Mobile" value={mr.mobile || "—"} />
              <InfoRow icon={MapPin} label="Region" value={mr.region || "—"} />
            </Card>
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Images className="w-4 h-4 text-slate-500" />
                <div className="font-display font-bold text-slate-900">Certificate Requests</div>
              </div>
              <div className="text-4xl font-extrabold text-slate-900 mb-3">{requests.length}</div>
              <div className="text-xs text-slate-500 mb-3">
                Counter: {mr.request_seq_counter ?? requests.length}/{mr.request_limit || 6}
              </div>
              {mr.role === "mr" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mb-3 h-8 gap-1"
                  onClick={resetRequestCount}
                  disabled={resetBusy}
                >
                  <UnlockKeyhole className="w-3.5 h-3.5" /> {resetBusy ? "Resetting..." : "Unlock ID (Reset to 0)"}
                </Button>
              )}
              <div className="flex flex-wrap gap-2">
                {["pending", "printing", "framing", "dispatched"].map((s) => (
                  <span key={s} className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[s]}`}>
                    {s}: {counts[s] || 0}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          {/* Requests table */}
          <div className="text-sm font-semibold text-slate-700 mb-2">Requests sent by this MR</div>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Request ID</th>
                    <th className="px-4 py-3">Doctor</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3 text-right">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-400">No requests yet.</td></tr>
                  )}
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.display_id}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{r.doctor_name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                          {r.status_label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString()}<br/>
                        <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setPreviewId(r.id)} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Certificate Preview</DialogTitle></DialogHeader>
          {previewId && (
            <img src={`${API}/requests/${previewId}/preview?t=${Date.now()}`} alt="Certificate preview" className="w-full rounded-md" />
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-4 h-4 text-slate-400 shrink-0" />
    <div className="text-xs uppercase tracking-wider text-slate-500 w-24 shrink-0">{label}</div>
    <div className="text-sm text-slate-900 font-medium truncate">{value}</div>
  </div>
);
